// src/Components/Epo/SwapVendorModal.js
//
// Round 22 — Modal for swapping an EPO's vendor. Triggered from the timeline
// page by Finance/Babu/IT-Admin. Loads the vendor list (same endpoint
// CreateEpoForm uses) and presents a searchable dropdown.

import React, { useEffect, useState } from "react";
import {
    Modal, ModalHeader, ModalBody, ModalFooter,
    Button, Alert, FormGroup, Label
} from "reactstrap";
import Select from "../Common/ThemedSelect"; // Round THEME-2: follows the theme
import api from "../../api";
import { swapVendor } from "../../services/epoVendorApi";

const errMsg = (e) =>
    e?.response?.data?.Message ??
    e?.response?.data?.message ??
    e?.message ??
    "Action failed.";

const SwapVendorModal = ({ isOpen, toggle, epoNumber, currentVendor, onSwapped }) => {
    const [vendorList, setVendorList] = useState([]);
    const [loadingVendors, setLoadingVendors] = useState(false);
    const [selected, setSelected] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    // Load the vendor list on first open. CreateEpoForm uses the same endpoint;
    // we don't share state to keep the modal self-contained.
    useEffect(() => {
        if (!isOpen) return;
        let alive = true;
        setLoadingVendors(true);
        setError(null);
        setSelected(null);
        // Same endpoint CreateEpoForm uses (R22 parity).
        api.get("registration/by-company?co=SSW")
            .then(({ data }) => {
                if (!alive) return;
                const list = Array.isArray(data) ? data : (data?.results ?? []);
                setVendorList(list);
            })
            .catch((e) => { if (alive) setError(errMsg(e)); })
            .finally(() => { if (alive) setLoadingVendors(false); });
        return () => { alive = false; };
    }, [isOpen]);

    const options = vendorList.map(v => ({
        value: v.vendorId ?? v.VendorId,
        label: `${v.vendorId ?? v.VendorId} | ${v.vendor ?? v.Vendor ?? v.company ?? v.Company} | ${v.city ?? v.City ?? ""}, ${v.state ?? v.State ?? ""}`,
        raw: v,
    }));

    const submit = async () => {
        if (!selected) {
            setError("Pick a vendor first.");
            return;
        }
        setBusy(true); setError(null);
        try {
            const v = selected.raw;
            await swapVendor(epoNumber, {
                vendorId: selected.value,
                company:  v.vendor   ?? v.Vendor  ?? v.company ?? v.Company ?? "",
                street1:  v.street1  ?? v.Street1 ?? "",
                city:     v.city     ?? v.City    ?? "",
                state:    v.state    ?? v.State   ?? "",
                zip:      v.zip      ?? v.Zip     ?? "",
            });
            if (typeof onSwapped === "function") onSwapped();
            toggle();
        } catch (e) {
            setError(errMsg(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={busy ? undefined : toggle} centered>
            <ModalHeader toggle={busy ? undefined : toggle}>
                Swap Vendor — EPO #{epoNumber}
            </ModalHeader>
            <ModalBody>
                <div className="mb-3 small text-muted">
                    <div><strong>Current vendor:</strong> {currentVendor || "—"}</div>
                </div>
                {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}
                <FormGroup>
                    <Label>New vendor</Label>
                    <Select
                        options={options}
                        value={selected}
                        onChange={setSelected}
                        isLoading={loadingVendors}
                        placeholder="Search vendors by id, name, or city…"
                        isClearable
                    />
                </FormGroup>
                <div className="small text-muted">
                    Swap will update VendorId, Company, Street, City, State, and Zip on the EPO header.
                    Line items, totals, and approval history are untouched.
                </div>
            </ModalBody>
            <ModalFooter>
                <Button color="light" onClick={toggle} disabled={busy}>Cancel</Button>
                <Button color="primary" onClick={submit} disabled={busy || !selected}>
                    {busy ? "Swapping…" : "Swap Vendor"}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default SwapVendorModal;
